import React, { useState } from 'react';
import { comprarMoeda, definirMoedaAtiva, gastarNaMoeda, venderMoeda } from '../services/api';
import { Category } from '../types/Category';
import { CarteiraDeMoeda, EstadoDaMoeda } from '../types/Moeda';
import { brlOrMasked, MASKED_VALUE } from '../utils/format';
import { LIMITES } from '../utils/limites';
import { mensagemDeErro } from '../utils/mensagemDeErro';
import { MOEDAS, lerQuantia, naMoeda, nomeDaMoeda } from '../utils/moeda';
import './OutrasMoedas.css';

/**
 * Outras moedas (13/10) — a moeda estrangeira que ela comprou para viajar.
 *
 * A compra tira o dinheiro do caixa uma vez. Cada gasto na moeda vira despesa
 * em reais, pelo que ela PAGOU pela moeda, sem tirar do caixa de novo. Por
 * isso cada carteira mostra três números juntos: o que resta, quanto isso
 * custou e o preço médio, que é a conta por trás dos outros dois.
 *
 * A seção só existe para quem tem ou já teve moeda (quem decide é o App). As
 * contas são do servidor; aqui nada é somado.
 */

type Acao = 'compra' | 'venda' | 'gasto';

interface Props {
  estado: EstadoDaMoeda;
  categories: Category[];
  hideValues?: boolean;
  /** Comprou, gastou, vendeu ou mudou o modo: o saldo e a lista precisam reler. */
  onChange: () => void;
}

const dataBR = (iso: string) => { const [a, m, d] = iso.slice(0, 10).split('-'); return `${d}/${m}/${a}`; };
const capital = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

export const OutrasMoedas: React.FC<Props> = ({ estado, categories, hideValues, onChange }) => {
  const semCarteira = estado.carteiras.length === 0;
  /** O formulário aberto: uma ação numa moeda. `moeda: null` = comprar uma moeda nova. */
  const [form, setForm] = useState<{ acao: Acao; moeda: string | null } | null>(semCarteira ? { acao: 'compra', moeda: null } : null);
  const [moedaNova, setMoedaNova] = useState('EUR');
  const [quantia, setQuantia] = useState('');
  const [reais, setReais] = useState('');
  const [descricao, setDescricao] = useState('');
  const [categoria, setCategoria] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  /** A moeda cujos movimentos estão abertos. */
  const [vendo, setVendo] = useState<string | null>(null);

  const dinheiro = (v: number) => brlOrMasked(v, !!hideValues);
  const emMoeda = (v: number, moeda: string) => (hideValues ? MASKED_VALUE : naMoeda(v, moeda));

  const abrir = (acao: Acao, moeda: string | null) => {
    setForm({ acao, moeda });
    setQuantia(''); setReais(''); setDescricao(''); setCategoria(''); setErro(null);
  };

  const salvar = async () => {
    if (!form) return;
    const moeda = form.moeda ?? moedaNova;
    const q = lerQuantia(quantia);
    const r = lerQuantia(reais);
    if (!q) { setErro(`Informe a quantia em ${nomeDaMoeda(moeda)}.`); return; }
    if (form.acao !== 'gasto' && !r) { setErro('Informe o valor em reais.'); return; }
    if (form.acao === 'gasto' && !descricao.trim()) { setErro('Diga com o que foi o gasto.'); return; }
    setSalvando(true);
    try {
      if (form.acao === 'compra') await comprarMoeda(moeda, q, r!);
      else if (form.acao === 'venda') await venderMoeda(moeda, q, r!);
      else await gastarNaMoeda(moeda, q, descricao.trim(), categoria || null);
      setForm(null);
      onChange();
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui salvar.'));
    } finally {
      setSalvando(false);
    }
  };

  const mudarModo = async (moeda: null) => {
    try {
      await definirMoedaAtiva(moeda);
      onChange();
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui mudar a moeda dos lançamentos.'));
    }
  };

  const formulario = (c: CarteiraDeMoeda | null) => {
    const moeda = form!.moeda ?? moedaNova;
    const q = lerQuantia(quantia);
    const r = lerQuantia(reais);
    return (
      <div className="moe-form">
        {form!.moeda === null && (
          <label>
            Moeda
            <select value={moedaNova} onChange={e => setMoedaNova(e.target.value)}>
              {MOEDAS.map(m => <option key={m} value={m}>{capital(nomeDaMoeda(m))}</option>)}
            </select>
          </label>
        )}
        {form!.acao === 'gasto' && (
          <label>
            Com o que foi
            <input
              type="text" value={descricao} maxLength={LIMITES.descricao} autoFocus
              onChange={e => setDescricao(e.target.value)} placeholder="Jantar"
            />
          </label>
        )}
        <label>
          {form!.acao === 'compra' ? `Quanto comprou, em ${nomeDaMoeda(moeda)}` : form!.acao === 'venda' ? `Quanto vendeu, em ${nomeDaMoeda(moeda)}` : `Valor em ${nomeDaMoeda(moeda)}`}
          <input inputMode="decimal" value={quantia} onChange={e => setQuantia(e.target.value)} placeholder={form!.acao === 'gasto' ? '20' : '500'} />
        </label>
        {form!.acao !== 'gasto' && (
          <label>
            {form!.acao === 'compra' ? 'Quanto pagou, em reais' : 'Quanto recebeu, em reais'}
            <input inputMode="decimal" value={reais} onChange={e => setReais(e.target.value)} placeholder="3000" />
          </label>
        )}
        {form!.acao === 'gasto' && categories.length > 0 && (
          <label>
            <span>Categoria <span className="moe-opcional">(opcional)</span></span>
            <select value={categoria} onChange={e => setCategoria(e.target.value)}>
              <option value="">Sem categoria</option>
              {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
            </select>
          </label>
        )}
        {/* A conta, antes de salvar: é o que evita trocar o total pela cotação. */}
        {form!.acao === 'compra' && q && r && (
          <p className="moe-detalhe">Dá {dinheiro(r / q)} por {nomeDaMoeda(moeda)}. Esse valor sai do seu saldo agora.</p>
        )}
        {form!.acao === 'gasto' && q && c?.precoMedio && (
          <p className="moe-detalhe">Entra como {dinheiro(q * c.precoMedio)} nas suas despesas, sem sair do saldo de novo.</p>
        )}
        {erro && <p className="moe-erro">{erro}</p>}
        <div className="moe-botoes">
          <button type="button" className="moe-salvar" disabled={salvando} aria-busy={salvando} onClick={salvar}>
            Salvar
          </button>
          <button type="button" className="moe-acao" onClick={() => setForm(null)}>Cancelar</button>
        </div>
      </div>
    );
  };

  return (
    /*
     * A mesma moldura dos outros cards do quadro (13/10, com print dela: "não
     * está seguindo o design dos outros cards"): fundo próprio, barra no topo,
     * título com contador e a ação principal no cabeçalho, como Lembretes e
     * Investimentos.
     */
    <section className="moe" aria-label="Outras moedas">
      <div className="moe-header">
        <div className="moe-title-group">
          <h2>Outras moedas</h2>
          {estado.carteiras.length > 0 && <span className="moe-count-badge">{estado.carteiras.length}</span>}
        </div>
        {!(form && form.moeda === null) && (
          <button type="button" className="moe-btn-add" onClick={() => abrir('compra', null)}>
            + Nova moeda
          </button>
        )}
      </div>

      {semCarteira && (
        <p className="moe-vazio">
          Comprou moeda para viajar? Registre aqui: o valor sai do seu saldo uma vez, e cada gasto lá fora
          entra nas suas despesas pelo preço que você pagou.
        </p>
      )}

      {form && form.moeda === null && <div className="moe-item moe-item-form">{formulario(null)}</div>}

      <div className="moe-list">
        {estado.carteiras.map(c => {
          const ligada = estado.ativa === c.moeda;
          const movimentos = estado.movimentos.filter(m => m.moeda === c.moeda);
          const gastos = estado.gastos.filter(g => g.moeda === c.moeda);
          return (
            <div key={c.moeda} className="moe-item">
              <div className="moe-item-topo">
                <span className="moe-item-badge">{c.moeda}</span>
                <div className="moe-item-body">
                  <span className="moe-item-name">
                    {capital(nomeDaMoeda(c.moeda))}
                    {ligada && <span className="moe-selo">lançando nesta moeda</span>}
                  </span>
                  <span className="moe-item-sub">
                    {c.saldo > 0 && c.precoMedio !== null
                      ? `custaram ${dinheiro(c.custoDoSaldo)} · ${dinheiro(c.precoMedio)} por ${nomeDaMoeda(c.moeda)}`
                      : 'acabou'}
                  </span>
                </div>
                <div className="moe-item-valor">
                  <span className="moe-item-rotulo">Restam</span>
                  <strong className="moe-saldo">{emMoeda(c.saldo, c.moeda)}</strong>
                </div>
              </div>

              {!(form && form.moeda === c.moeda) && (
                <div className="moe-acoes">
                  {c.saldo > 0 && <button type="button" className="moe-chip" onClick={() => abrir('gasto', c.moeda)}>Gastei</button>}
                  <button type="button" className="moe-chip" onClick={() => abrir('compra', c.moeda)}>Comprei mais</button>
                  {c.saldo > 0 && <button type="button" className="moe-chip" onClick={() => abrir('venda', c.moeda)}>Vendi a sobra</button>}
                  {/*
                    Só a SAÍDA do modo viagem mora aqui. Ligar é pelo WhatsApp,
                    que é onde ele vale: o botão "Lançar tudo em euro" prometia
                    mudar o formulário do app, que segue em real (ela, 13/10).
                  */}
                  {ligada && <button type="button" className="moe-chip" onClick={() => mudarModo(null)}>Voltar ao real</button>}
                  <button
                    type="button" className="moe-acao moe-ver" aria-expanded={vendo === c.moeda}
                    onClick={() => setVendo(vendo === c.moeda ? null : c.moeda)}
                  >
                    {vendo === c.moeda ? 'Esconder movimentos' : `Ver movimentos (${movimentos.length + gastos.length})`}
                  </button>
                </div>
              )}
              {form && form.moeda === c.moeda && formulario(c)}

              {vendo === c.moeda && (
                <ul className="moe-lista">
                  {movimentos.map(m => (
                    <li key={m.id}>
                      <span>{dataBR(m.data)} · {m.tipo === 'compra' ? 'Compra' : 'Venda'}</span>
                      <span>{m.tipo === 'compra' ? '+' : '−'} {emMoeda(m.quantidade, m.moeda)} · {dinheiro(m.reais)}</span>
                    </li>
                  ))}
                  {gastos.map(g => (
                    <li key={g.id}>
                      <span>{dataBR(g.data)} · {g.descricao}</span>
                      <span>− {emMoeda(g.valorNaMoeda, g.moeda)} · {dinheiro(g.reais)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
      {!form && erro && <p className="moe-erro">{erro}</p>}
    </section>
  );
};
